<template>
    <div class="satalia-report">
        <div class="preflight-info">
            <h4><span>PRE-FLIGHT</span></h4>

            <div class="preflight-info-table">
                <div class="preflight-info-table-row">
                    <span>Analysis requested at:</span>
                    <span>{{ new Date(created).toLocaleString() }}</span>
                </div>
                <div v-if="report.completedAt" class="preflight-info-table-row">
                    <span>Analysis completed at:</span>
                    <span>{{ new Date(report.completedAt).toLocaleString() }}</span>
                </div>
            </div>
        </div>

        <div v-if="report.bestPractices?.length" class="preflight-guidelines">
            <h4>Best Practice Checks by Platform</h4>

            <Tabs v-model="activeTab" type="card" :style="{ '--tab-height': activeTabHeight + 'px' }">
                <TabPane
                    v-for="platform in platforms"
                    :key="platform"
                    :label="getPlatformTabLabel(platform)"
                    :name="platform"
                >
                    <div :ref="el => setTabRef(platform, el)" class="tab-content">
                        <div class="platform-summary">
                            <div class="platform-summary-item">
                                <span>Total checks:</span>
                                <span>{{ getPlatformSummary(platform).total }}</span>
                            </div>
                            <div class="platform-summary-item">
                                <span>Passed:</span>
                                <span class="status-pass">{{ getPlatformSummary(platform).passed }}</span>
                            </div>
                            <div class="platform-summary-item">
                                <span>Failed:</span>
                                <span class="status-fail">{{ getPlatformSummary(platform).failed }}</span>
                            </div>
                            <div class="platform-summary-item">
                                <span>Warnings:</span>
                                <span class="status-warning">{{ getPlatformSummary(platform).warnings }}</span>
                            </div>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th>Rule</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(check, index) in getChecksByPlatform(platform)"
                                    :key="index"
                                    :class="`check-${check.result}`"
                                >
                                    <td>
                                        <div class="rule-name">{{ check.rule_name }}</div>
                                        <div class="rule-explanation">
                                            <div v-for="(line, idx) in formatExplanation(check.explanation)" :key="idx">
                                                {{ line }}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ check.category }}</td>
                                    <td class="status-cell">
                                        <Icon v-if="check.result === 'pass'" type="md-checkmark-circle" color="green" />
                                        <Icon v-else-if="check.result === 'fail'" type="md-close-circle" color="red" />
                                        <Icon v-else type="md-warning" color="orange" />
                                        <span>{{ check.result }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </TabPane>
            </Tabs>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from "vue";
import type { ComponentPublicInstance } from "vue";
import type { SataliaPreflightHistory } from "@/utils/Preflight/getPreflightHistory";

const props = defineProps<{
    report: Extract<SataliaPreflightHistory, { status: "COMPLETED" }>["report"];
    created: string;
}>();

const activeTab = ref("");
const tabRefs = ref<Record<string, HTMLElement>>({});
const activeTabHeight = ref(0);

const platforms = computed(() => {
    if (!props.report?.bestPractices) return [];
    const platformSet = new Set(props.report.bestPractices.map(check => check.platform));
    return Array.from(platformSet).sort();
});

watch(
    platforms,
    newPlatforms => {
        if (newPlatforms.length > 0 && !activeTab.value) {
            activeTab.value = newPlatforms[0];
        }
    },
    { immediate: true }
);

watch(
    activeTab,
    async newTab => {
        if (newTab) {
            await nextTick();
            const activeEl = tabRefs.value[newTab];
            if (activeEl) {
                const height = activeEl.scrollHeight;
                activeTabHeight.value = height;
            }
        }
    },
    { immediate: true }
);

const setTabRef = (platform: string, el: Element | ComponentPublicInstance | null) => {
    if (el && el instanceof HTMLElement) {
        tabRefs.value[platform] = el;
    }
};

const getChecksByPlatform = (platform: string) => {
    if (!props.report?.bestPractices) return [];
    return props.report.bestPractices.filter(check => check.platform === platform);
};

const getPlatformSummary = (platform: string) => {
    const checks = getChecksByPlatform(platform);
    return {
        total: checks.length,
        passed: checks.filter(c => c.result === "pass").length,
        failed: checks.filter(c => c.result === "fail").length,
        warnings: checks.filter(c => c.result === "warning").length
    };
};

const getPlatformTabLabel = (platform: string) => {
    const platformSummary = getPlatformSummary(platform);
    return `${platform} (${platformSummary.passed}/${platformSummary.failed}/${platformSummary.warnings})`;
};

const formatExplanation = (explanation: string): string[] => {
    try {
        const parsed = JSON.parse(explanation);
        const lines: string[] = [];

        if (parsed.feature) {
            lines.push(`Feature: ${parsed.feature}`);
        }
        if (parsed.expected !== undefined) {
            lines.push(`Expected: ${parsed.expected}`);
        }
        if (parsed.actual !== undefined) {
            lines.push(`Actual: ${parsed.actual}`);
        }

        return lines.length > 0 ? lines : [explanation];
    } catch {
        return [explanation];
    }
};
</script>

<style scoped>
.satalia-report {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.preflight-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.preflight-info h4 {
    margin: 0 0 12px;
}

.preflight-info-table {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.preflight-info-table-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.preflight-info-table-row > span:first-child {
    font-weight: 600;
}

.preflight-guidelines {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.preflight-guidelines h4 {
    margin: 0 0 8px;
}

.platform-summary {
    display: flex;
    gap: 16px;
    padding: 12px;
    background-color: var(--wpp-grey-color-100);
    border-radius: 4px;
    margin-bottom: 16px;
}

.platform-summary-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.platform-summary-item > span:first-child {
    font-weight: 600;
}

.tab-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preflight-guidelines th,
.preflight-guidelines td {
    padding: 8px;
    text-align: left;
}

.rule-name {
    font-weight: 600;
    margin-bottom: 4px;
}

.rule-explanation {
    font-size: 11px;
    color: var(--wpp-grey-color-600);
}

.rule-explanation > div {
    line-height: 1.5;
}

.status-cell {
    display: flex;
    align-items: center;
    gap: 4px;
}

.status-pass {
    color: var(--wpp-success-color-500);
    font-weight: 600;
}

.status-fail {
    color: var(--wpp-danger-color-500);
    font-weight: 600;
}

.status-warning {
    color: var(--wpp-warning-color-500);
    font-weight: 600;
}

.check-pass {
    background-color: var(--wpp-success-color-50);
}

.check-fail {
    background-color: var(--wpp-danger-color-50);
}

.check-warning {
    background-color: var(--wpp-warning-color-50);
}

:deep(.ivu-tabs-content) {
    height: var(--tab-height);
}

body:has(> .wpp-theme-dark) {
    .platform-summary {
        background-color: var(--wpp-grey-color-900);
    }
}
</style>
